import { Control, Controller } from "react-hook-form";
import TextField from "../../TextField";
import React from "react";
import { useI18n } from "../../../hooks/i18n-hooks";
import { FormData, useValidateNumber } from "./PayrollBreakdownEditor";
import { BodyMediumText, BodySmallText, LinkSmallText } from "../../FormattedText";
import Gap from "../../../ui_atoms/Gap";
import { useTheme } from "../../../hooks/use-theme";
import { useNavigation } from "@react-navigation/core";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../../navigations/RootNavigation";
import { MissionDeduction, MissionFee } from "../../../__generated__/graphql";
import { ChainTextInputProps } from "../../../hooks/use-chain-input-focus";
import FullWidthLine from "../../FullWidthLine";

type ScreenParams = StackScreenProps<RootStackParamList>;

type PayrollBreakdownCommonProps = {
    needWorkDays: boolean;
    control: Control<FormData>;
    fees: MissionFee[];
    deductions: MissionDeduction[];
    chainInputFocus?: (index: number) => ChainTextInputProps;
    maxWorkDays?: number;
};

const PayrollBreakdownCommon: React.FC<PayrollBreakdownCommonProps> = ({
    needWorkDays,
    control,
    fees,
    deductions,
    chainInputFocus = () => ({}),
    maxWorkDays,
}) => {
    const i18n = useI18n();
    const validateNumber = useValidateNumber();
    const { colors } = useTheme();
    const navigator = useNavigation<ScreenParams["navigation"]>();

    return (
        needWorkDays && (
            <>
                <BodyMediumText>
                    {i18n.t("payroll_breakdown.field_total_work_days_title")}
                </BodyMediumText>
                <Gap gap={1 / 3} />
                <Controller
                    name="totalWorkedDays"
                    control={control}
                    rules={{
                        validate: {
                            isNumber: validateNumber,
                            isPositive: (value) => {
                                if (value && Number(value) < 0) {
                                    return i18n.t("payroll_breakdown.field_total_work_days_error");
                                }
                                return true;
                            },
                            isLessThanMax: (value) => {
                                if (maxWorkDays && value && Number(value) > maxWorkDays) {
                                    return i18n.t("payroll_breakdown.field_total_work_days_error", {
                                        max: maxWorkDays,
                                    });
                                }
                                return true;
                            },
                        },
                    }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <TextField
                            {...chainInputFocus(0)}
                            label={i18n.t("payroll_breakdown.field_total_work_days")}
                            value={value}
                            onChangeText={onChange}
                            error={error?.message}
                            showErrorText
                            isDecimalNumber
                            keyboardType="numeric"
                            style={{ alignSelf: "flex-start" }}
                            inputStyle={{
                                textAlign: "right",
                                width: undefined,
                                minWidth: 0,
                            }}
                        />
                    )}
                />
                <Gap gap={1 / 3} />
                <BodySmallText color={colors.greyAccessible}>
                    {i18n.t("payroll_breakdown.field_total_work_days_info")}
                </BodySmallText>
                <Gap gap={0.5} />
                {(fees.length > 0 || deductions.length > 0) && (
                    <LinkSmallText
                        onPress={() => {
                            navigator.navigate("FeesDetails", { fees, deductions });
                        }}
                    >
                        {i18n.t("payroll_breakdown.fees_and_deductions")}
                    </LinkSmallText>
                )}
                <Gap gap={1} />
                <FullWidthLine />
                <Gap gap={1} />
            </>
        )
    );
};

export default PayrollBreakdownCommon;
